import {createSlice, isAnyOf, PayloadAction, CaseReducer, type SliceSelectors} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {FDSPhase} from '@web/core/constants'
import {isMobileDisplay} from '@web/helpers/userAgent'
import {
    start,
    prepare,
    processOrder,
    processVan,
    processFDS,
    apply,
    abort,
    finish,
    finishFDS,
    startFDS,
    clear,
    startApply,
    setAutoPay,
    CauseCode,
    FinishCode,
    FDSAction,
    OrderProcessPhase,
    ProcessingStep,
    ProcessingStepType,
    FinishStatus,
    getUnknownFinished,
    getInitialFdsState,
    getInitialState,
    getNextStep,
    OrderProcessorState,
} from '@web/slices/orderProcessor/slice'

import {OrderProcessorReducers, MakeCreateSliceOptions} from './interfaces'

const nextStepActions = isAnyOf(
    prepare.fulfilled,
    processOrder.fulfilled,
    processVan.fulfilled,
    startApply.fulfilled,
    apply.fulfilled,
)
const finishActions = isAnyOf(
    finish,
    abort,
    prepare.rejected,
    processOrder.rejected,
    processVan.rejected,
    processFDS.rejected,
    finishFDS.rejected,
    startApply.rejected,
    apply.rejected,
)

/** 다음 스탭을 진행할 수 있는 action type 과 step 조합 */
const PossibleNextPairs = [
    [prepare.fulfilled.type, ProcessingStep.PREPARE],
    [processOrder.fulfilled.type, ProcessingStep.PROCESSING],
    [processVan.fulfilled.type, ProcessingStep.VAN_CHECK],
    [startApply.fulfilled.type, ProcessingStep.FDS_CHECK],
    [apply.fulfilled.type, ProcessingStep.APPLYING],
]

function isPossibleNext(type: string, processStep: ProcessingStepType) {
    const typeEqualsTo = eq(type)
    const stepEqualsTo = eq(processStep)

    return PossibleNextPairs.some(([nextType, nextStep]) => typeEqualsTo(nextType) && stepEqualsTo(nextStep))
}

function shouldNextStep(state: OrderProcessorState) {
    if (!(state.phase === OrderProcessPhase.IDLE || state.phase === OrderProcessPhase.FINISH)) {
        const processStep = getNextStep(state.processSteps, state.currentProcessStep, ProcessingStep.IDLE)

        if (processStep === ProcessingStep.APPLYING) {
            state.phase = OrderProcessPhase.PROCESSING
        }
        state.currentProcessStep = processStep
    }
}

const finishOrderProcess: CaseReducer<OrderProcessorState, PayloadAction<FinishStatus>> = (state, {payload}) => {
    const finishStatus = payload ?? getUnknownFinished()
    if (finishStatus.code === CauseCode.SKIP) {
        shouldNextStep(state)
        return
    }

    const orderProcessNo = (function () {
        if (state.orderProcessNo) {
            return state.orderProcessNo
        }
        if (finishStatus.content?.failure && 'orderProcessId' in finishStatus.content.failure) {
            return finishStatus.content.failure.orderProcessId
        }
        return ''
    })()

    const completed = (function () {
        return finishStatus.content?.state === 'ORDER_COMPLETED' || finishStatus.code === FinishCode.COMPLETED
    })()

    return {
        ...getInitialState(),
        phase: OrderProcessPhase.FINISH,
        completed,
        orderNo: completed ? state.orderNo : '',
        channel: completed ? state.channel : null,
        finishStatus: {
            ...finishStatus,
            orderProcessNo,
        },
        verificationKey: state.verificationKey,
        deviceLockType: state.deviceLockType,
        fds: finishStatus.code === FinishCode.FDS_BLOCKED ? state.fds : getInitialFdsState(),
    }
}

const nextOrderProcess: CaseReducer<OrderProcessorState, PayloadAction> = (state, {type}) => {
    if (isPossibleNext(type, state.currentProcessStep)) {
        shouldNextStep(state)
    }
}

export default (options?: MakeCreateSliceOptions) =>
    createSlice<OrderProcessorState, OrderProcessorReducers, 'orderProcessor', SliceSelectors<OrderProcessorState>>({
        name: 'orderProcessor',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(start.pending, (state) => {
                    // fds focus
                    state.requestStartOrderCount += 1
                })
                .addCase(start.fulfilled, (state, {payload}) => {
                    const {steps, firstStep, orderRequest} = payload

                    const fds = getInitialFdsState()
                    fds.actionType = isMobileDisplay ? FDSAction.REPLACE : FDSAction.POPUP

                    return {
                        ...getInitialState(),
                        orderRequest,
                        fds,
                        phase: OrderProcessPhase.START,
                        currentProcessStep: firstStep,
                        processSteps: steps,
                        orderNo: 'orderNo' in orderRequest.essence ? orderRequest.essence.orderNo : '',
                        channel: orderRequest.entry ?? null,
                        channelGroup: orderRequest.channelGroup ?? null,
                        applyChannel: orderRequest.applyChannel ?? null,
                        verificationKey: state.verificationKey,
                        deviceLockType: state.deviceLockType,
                    }
                })
                .addCase(prepare.fulfilled, (state, {payload}) => {
                    if ('orderProcessId' in payload) {
                        state.prepare = payload
                        state.orderProcessNo = payload.orderProcessId
                    } else if ('prepareFamilyPayId' in payload) {
                        const {prepareFamilyPayId, token} = payload
                        state.orderProcessNo = prepareFamilyPayId
                        state.prepare.orderProcessId = payload.prepareFamilyPayId
                        state.fds.authentication = {
                            level: token.authenticationLevel,
                            sessionKey: token.sessionKey,
                            unavailablePaymentType: token.fdsUnavailablePaymentType,
                            fdsUuid: token.fdsUuid,
                        }
                    }
                })
                .addCase(processOrder.fulfilled, (state, {payload}) => {
                    if (payload && payload.orderAuthentication) {
                        state.orderProcessResult = payload
                        state.orderNo = payload.orderNo ?? ''
                        state.fds.authentication = {
                            level: payload.orderAuthentication.authenticationLevel,
                            sessionKey: payload.orderAuthentication.sessionKey,
                            unavailablePaymentType: payload.orderAuthentication.fdsUnavailablePaymentType,
                            fdsUuid: payload.orderAuthentication.fdsUuid,
                        }
                    }
                })
                .addCase(processVan.fulfilled, (state, {payload}) => {
                    state.applyOrderRequest = payload
                })
                .addCase(processFDS.fulfilled, (state, {payload}) => {
                    const {url} = payload
                    state.fds.url = url
                    state.fds.phase = FDSPhase.STARTED
                    state.fds.authentication = state.fds.authentication
                        ? {
                              ...state.fds.authentication,
                              sessionKey: payload?.sessionKey ?? null,
                          }
                        : null
                })
                .addCase(startFDS.fulfilled, (state) => {
                    state.fds.phase = FDSPhase.PROCESSING
                })
                .addCase(finishFDS.fulfilled, (state) => {
                    state.fds.phase = FDSPhase.FINISH
                })
                .addCase(clear, (state) => {
                    state.finishStatus = null
                })
                .addCase(startApply.fulfilled, (state, {payload}) => {
                    state.orderSheetId = payload.orderSheetId
                    state.orderProcessNo = payload.orderProcessId
                    state.orderNo = payload.orderNo ?? ''
                    state.prepare = {
                        orderProcessId: payload.orderProcessId,
                        orderSessionIoAccessUrl: payload.orderSessionIoAccessUrl,
                        orderSessionIoChannel: payload.orderSessionIoChannel,
                        applySessionIoAccessUrl: payload.applySessionIoAccessUrl,
                        applySessionIoChannel: payload.applySessionIoChannel,
                    }
                    state.channel = payload.channel
                    state.channelGroup = payload.channelGroup
                    state.currentProcessStep = ProcessingStep.FDS_CHECK
                    state.processSteps = payload.processSteps
                    state.phase = OrderProcessPhase.PROCESSING
                    state.fds.phase = FDSPhase.FINISH
                    state.applyOrderRequest = payload.applyOrderRequest ?? {}
                    state.orderProcessResult = {
                        orderProcessId: payload.orderProcessId,
                        orderSheetId: payload.orderSheetId,
                        status: payload.state,
                        orderNo: payload.orderNo,
                        orderAuthentication: payload.orderAuthentication,
                    }
                })
                .addCase(setAutoPay, (state, {payload}) => {
                    state.isAutoPay = payload
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher<PayloadAction>(nextStepActions, nextOrderProcess)
                .addMatcher<PayloadAction<FinishStatus>>(finishActions, finishOrderProcess)
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.orderProcessor ?? state
                })
        },
    })
